/**
 * Types for Remmi internal chat.
 *
 * These mirror `docs/backend/chat/internal-chat.md` field-for-field — the same snake_case keys the
 * API returns, so nothing has to be renamed on the way in. Where the UI needs extra state that
 * never comes from the server (a message still in flight, for example) the field is marked
 * client-only.
 */

/** Presence states. `offline` is never chosen by a user — it means "no open socket". */
export type ChatPresenceStatus = 'online' | 'dnd' | 'offline';

/** The two states a user can actually pick. */
export type ChatManualStatus = Extract<ChatPresenceStatus, 'online' | 'dnd'>;

/**
 * A richer, self-chosen reason for being unavailable — a client-only nicety layered on top of the
 * two states the backend actually persists and broadcasts. `POST /chat/presence` accepts only
 * `"online" | "dnd"` (`docs/backend/chat/internal-chat.md` §6.6), so every preset still buckets down to
 * one of those two: colleagues only ever see your plain green/orange dot. What the preset buys is
 * local — your own status trigger remembers *why*, kept in this browser's storage since the server
 * has no column for it.
 */
export interface ChatStatusPreset {
  id: string;
  label: string;
  description: string;
  bucket: ChatManualStatus;
}

/** Exactly the three states the office asked for — each with its own colour, not just its bucket. */
export const CHAT_STATUS_PRESETS: ChatStatusPreset[] = [
  { id: 'online', label: 'Available', description: 'Available to receive messages', bucket: 'online' },
  { id: 'dnd', label: 'Do Not Disturb', description: 'Messages still arrive, without a sound', bucket: 'dnd' },
  { id: 'away', label: 'Away', description: 'Stepped away — messages still arrive, without a sound', bucket: 'dnd' },
];

/** Local-only, keyed per browser rather than per account — the server has nowhere to keep it. */
export const CHAT_STATUS_PRESET_STORAGE_KEY = 'chat_status_preset';

/** What each bucket falls back to when the stored preset no longer matches the server's truth. */
export const CHAT_DEFAULT_STATUS_PRESET_ID: Record<ChatManualStatus, string> = { online: 'online', dnd: 'dnd' };

export type ChatConversationType = 'direct' | 'group';

/** The sidebar's quick filters. `all` is the default and shows every section. */
/** `hidden` only ever appears as a chip for a Master Admin — see {@link ChatStore.filterOptions}. */
export type ChatSidebarFilter = 'all' | 'unread' | 'online' | 'groups' | 'favourites' | 'calls' | 'hidden';

export interface ChatFilterOption {
  key: ChatSidebarFilter;
  label: string;
  /** Rendered as a count chip; `null` when a count would be noise (e.g. "All"). */
  count: number | null;
}

/**
 * One row of the sidebar — a colleague or a group, in a single interleaved list.
 *
 * They used to be two collapsible sections. Splitting them meant the thing you actually wanted
 * (whoever just messaged you) could be in either, and might be behind a collapsed header; one
 * list ordered by attention answers "who needs me?" in a glance, which is the question a chat
 * sidebar exists to answer.
 */
export type ChatListRow =
  | { kind: 'person'; id: string; person: ChatPerson; group?: undefined }
  | { kind: 'group'; id: string; group: ChatGroupSummary; person?: undefined };

export type ChatAttachmentKind = 'file' | 'image' | 'voice';

export type ChatSystemEvent = 'member_added' | 'member_removed' | 'group_renamed' | 'member_left' | null;

/** Last-message stub shown on a sidebar row. */
export interface ChatLastMessage {
  /**
   * Which message the preview belongs to.
   *
   * Added for `GET /chat/sync`, where it answers the one question a preview alone cannot: whether
   * a row moved because a *new message* arrived or because its unread count changed. Comparing ids
   * is exact, where comparing `created_at` is a guess about clock skew and second-granularity
   * timestamps. Optional because `GET /chat/conversations` may still answer without it.
   */
  message_id?: string;
  preview: string;
  created_at: string;
  sender_id: string;
  sender_name?: string;
  is_own: boolean;
}

/** A colleague in the People list. `conversation_id` is null until they have ever spoken. */
export interface ChatPerson {
  user_id: string;
  name: string;
  avatar_url: string | null;
  role: string;
  status: ChatPresenceStatus;
  last_seen_at?: string | null;
  conversation_id: string | null;
  unread_count: number;
  last_message?: ChatLastMessage | null;
  /**
   * False for a colleague who has since been deactivated.
   *
   * They are gone from `GET /chat/users` (so the pickers never offer them) but an existing DM
   * stays in the People list, otherwise its unread count could never be cleared. The row is
   * greyed and the composer is closed — see {@link ChatStore.activeIsInactive}.
   */
  is_active?: boolean;
  /**
   * True for a colleague a Master Admin has hidden from chat — see `docs/backend/chat/internal-chat-hide-user.md`.
   *
   * A different axis from {@link is_active}: this is an admin-triggered, reversible choice about
   * ANY colleague, not an inferred "this account is gone." Only a Master Admin's own sidebar read
   * ever carries `true` here for someone they haven't already messaged — see
   * {@link ChatStore.isHiddenPerson} for the single predicate every picker/list filters through.
   */
  is_hidden?: boolean;
}

/** A group row in the sidebar. */
export interface ChatGroupSummary {
  conversation_id: string;
  name: string;
  member_count: number;
  is_owner: boolean;
  unread_count: number;
  last_message?: ChatLastMessage | null;
}

/** Everything the sidebar renders, from one request (`GET /chat/conversations`). */
export interface ChatSidebarData {
  people: ChatPerson[];
  groups: ChatGroupSummary[];
  /** Always empty in V1 — the section is rendered so the expansion point is visible. */
  external_users: ChatPerson[];
  total_unread: number;
  /**
   * Whether the caller themself has been hidden from chat by a Master Admin.
   *
   * The only way this client can learn that about itself without waiting on a socket frame or a
   * rejected send — see {@link ChatStore.amHidden}, which a page load or reconnect reads this into.
   */
  my_is_hidden?: boolean;
}

/**
 * One row of `GET /chat/sync` — a conversation whose state has moved since the cursor.
 *
 * Deliberately thinner than a sidebar row: no roster, no presence, no member count, no avatar.
 * It answers "what changed" in two indexed queries rather than rebuilding the whole sidebar, which
 * is what makes it cheap enough to poll every thirty seconds. The cost of that thinness is that a
 * conversation the client holds no row for cannot be merged from this alone — see
 * {@link ChatStore.applySync}, which falls back to the full read for exactly that case.
 */
export interface ChatSyncConversation {
  conversation_id: string;
  type: ChatConversationType;
  name: string | null;
  unread_count: number;
  last_activity_at: string;
  last_message?: ChatLastMessage | null;
}

/** `GET /chat/sync?since=` — the delta the socket should have pushed. */
export interface ChatSyncResult {
  /**
   * Stamped **before** the queries ran, and what the next `since` must be.
   *
   * Using our own clock instead would drop any conversation that changed while the read was in
   * flight: it would be too new for this answer and too old for the next `since`. The server's
   * pre-query stamp overlaps the two windows rather than leaving a gap between them, so the worst
   * case is seeing a row twice — which the announced-message bookkeeping already handles.
   */
  server_time: string;
  total_unread: number;
  conversations: ChatSyncConversation[];
}

/**
 * `GET /chat/diagnostics` — which API process answered, and what sockets it holds.
 *
 * Loosely typed on purpose. This is a measurement tool, not a feature: it exists to settle whether
 * the realtime fault is architecture or code (`internal-chat-frontend-handover.md` §4), and the
 * only fields read here are the ones the verdict turns on. Anything else the server adds should
 * survive to the console untouched rather than be dropped by a type that was written too early.
 *
 * `api_processes_seen` / `peers` / `delivery_enabled` / `verdict` are the backend's fleet-wide
 * answer (`docs/backend/chat/internal-chat-realtime-delivery.md` §6, 25 Aug 2026) — every process
 * heartbeats, so any one of them can count the fleet and say so in one call. Older deployments
 * that predate this only ever return `instance_id`/`pid`/`caller`, which is why
 * {@link ChatStore.runDiagnostics} still falls back to sampling repeated calls when these are
 * absent.
 */
export interface ChatDiagnostics {
  /** Changes between calls ⇒ more than one API process is answering. */
  instance_id?: string;
  pid?: number;
  uptime_seconds?: number;
  /** Whether *this* caller's socket is held by the process that answered. */
  caller?: { connected_here?: boolean; socket_count?: number; [key: string]: unknown };
  /** Counted fleet-wide from heartbeats. `1` (or absent) means a single process — no sampling needed. */
  api_processes_seen?: number;
  peers?: { instance_id?: string; pid?: number; [key: string]: unknown }[];
  /** Whether `CHAT_BUS=mongo` cross-process delivery is currently on. */
  delivery_enabled?: boolean;
  /** A ready-made sentence describing the state — prefer this over the client's own wording. */
  verdict?: string;
  /**
   * The `internal-chat-presence-visibility.md` check, now run server-side
   * (`internal-chat-realtime-backend.md` §2b) instead of only via the console warning in
   * {@link ChatStore.reportPresenceGaps}. `count` above 0 means the sidebar roster and
   * `GET /chat/presence` have drifted apart again — independent of process topology, so it can be
   * non-zero even when `api_processes_seen` is 1.
   */
  presence_roster_gap?: { count?: number; user_ids?: string[]; sidebar_people?: number; presence_rows?: number };
  [key: string]: unknown;
}

/** What {@link ChatStore.runDiagnostics} concludes. */
export type ChatDiagnosticsVerdict =
  /** More than one process, and none of them can reach the others — messages/typing/presence drop. */
  | 'multi-process'
  /** More than one process, but `CHAT_BUS=mongo` is bridging them — this is the fixed, healthy state. */
  | 'multi-process-bridged'
  /** One process, but it does not hold our socket — the WS hostname routes somewhere else. */
  | 'socket-elsewhere'
  /** One process and it holds our socket — anything still broken is per-user. */
  | 'single-process'
  /** Nothing answered, so there is no verdict to give. */
  | 'unavailable';

export interface ChatDiagnosticsReport {
  verdict: ChatDiagnosticsVerdict;
  /** What to do about it, in the words of §4's table. */
  action: string;
  /** Every sample, so the raw numbers can be read rather than taken on trust. */
  samples: ChatDiagnostics[];
}

/** A participant inside a conversation. */
export interface ChatMember {
  user_id: string;
  name: string;
  avatar_url: string | null;
  status: ChatPresenceStatus;
  role: 'owner' | 'member';
  /** How far this member has read — lets "Seen by …" paint before any receipt frame arrives. */
  last_read_message_id?: string | null;
  last_read_at?: string | null;
  /**
   * False for a member who has since been deactivated. They keep their membership and their past
   * messages; the row is greyed so the list does not imply they are still reachable.
   */
  is_active?: boolean;
}

/** Conversation header + membership (`GET /chat/conversations/:id`). */
export interface ChatConversationDetail {
  conversation_id: string;
  type: ChatConversationType;
  name: string | null;
  created_by: { user_id: string; name: string } | null;
  created_at: string;
  is_owner: boolean;
  members: ChatMember[];
  my_unread_count: number;
  my_last_read_message_id: string | null;
}

export interface ChatAttachment {
  attachment_id: string;
  name: string;
  mime: string;
  size: number;
  /**
   * A **1-hour signed** S3 GET, minted fresh on every read — the bucket is private, so there is
   * no permanent URL. Never cache or persist it: a thread scrolled back into view after an hour
   * needs a re-fetch, which is what {@link ChatStore.refreshAttachments} does when an image 403s.
   */
  url: string;
  /**
   * The same object, signed with `response-content-disposition=attachment` — so an `<a href download>`
   * **saves** the file instead of opening it in a tab. Needs no auth header and carries the same
   * 1-hour TTL as {@link url}, so the same refresh-on-failure behaviour covers both.
   */
  download_url?: string | null;
  /**
   * The bearer-protected API route (what `download_url` used to be). Unusable from a plain
   * `<a>`/`<img>`/`<audio>`, which is why it is not what we render — kept for non-browser callers.
   */
  download_endpoint?: string | null;
  kind: ChatAttachmentKind;
  duration_ms?: number | null;
}

export interface ChatReaction {
  emoji: string;
  count: number;
  user_ids: string[];
  reacted_by_me: boolean;
}

export interface ChatMessageSender {
  user_id: string;
  name: string;
  avatar_url: string | null;
}

/**
 * Who did what to whom on a system row, so "John added Sarah" renders without a member lookup —
 * the actor or target may no longer be in the conversation at all.
 */
export interface ChatSystemMeta {
  actor_id?: string;
  actor_name?: string;
  target_ids?: string[];
  target_names?: string[];
  /** Rename events carry the two names. */
  old_name?: string | null;
  new_name?: string | null;
}

export interface ChatMessage {
  message_id: string;
  client_msg_id: string;
  conversation_id: string;
  sender: ChatMessageSender;
  body: string;
  attachments: ChatAttachment[];
  reactions: ChatReaction[];
  edited_at: string | null;
  is_deleted: boolean;
  system_event: ChatSystemEvent;
  system_meta?: ChatSystemMeta | null;
  created_at: string;

  /** Client-only: rendered but not yet acknowledged by the server. */
  pending?: boolean;
  /** Client-only: the send failed and the bubble offers a retry. */
  failed?: boolean;
}

/** One page of history (`GET /chat/conversations/:id/messages`), oldest-first. */
export interface ChatMessagePage {
  messages: ChatMessage[];
  has_more: boolean;
  oldest_message_id: string | null;
}

/**
 * What the reaction endpoints answer with — the recomputed array, **not** a whole message.
 * Applying it means patching by `message_id`, never upserting it as a message.
 */
export interface ChatReactionUpdate {
  message_id: string;
  reactions: ChatReaction[];
}

/** `POST /chat/conversations/:id/read` — the global badge plus this thread's own count. */
export interface ChatReadResult {
  total_unread: number;
  unread_count?: number;
}

/** `POST /chat/groups/:id/leave` — `deleted` is true when the leaver was the last member. */
export interface ChatLeaveResult {
  conversation_id?: string;
  deleted?: boolean;
  new_owner_id?: string | null;
}

/**
 * `POST /chat/presence` — `status` is the effective status (a user with no socket reads back
 * `offline`), `manual_status` is what was actually stored, which is what the switcher shows.
 */
export interface ChatPresenceResult {
  status?: ChatPresenceStatus;
  manual_status?: ChatManualStatus;
}

/**
 * An in-app notification for a message that arrived somewhere the user isn't looking.
 *
 * Distinct from the desktop notification in {@link ChatNotificationService}: that one only fires
 * for a backgrounded tab, and only if the user ever granted permission. This is the in-page
 * equivalent for a user who is on another screen of the app with the chat closed — no permission
 * to ask for, and it can be clicked straight through to the conversation.
 */
export interface ChatToast {
  /** Client-side id; the toast is dismissed and expired by it. */
  id: string;
  conversation_id: string;
  /** Sender's name, or "Sender · Group" so a group message says which one. */
  title: string;
  /** One line of the message, or the attachment's name. */
  body: string;
  avatar_url: string | null;
  /** Drives the avatar's initials fallback. */
  sender_name: string;
  is_group: boolean;
}

/** How long a toast stays before it withdraws on its own. */
export const CHAT_TOAST_TTL_MS = 4000;

/** Never stack more than this many — beyond it they cover the screen rather than inform. */
export const CHAT_TOAST_MAX = 3;

/**
 * Somewhere a message can be forwarded to — a colleague or a group.
 *
 * `conversation_id` is null for a colleague you have never messaged; the forward creates the DM
 * before posting. Deliberately not a `ChatPerson | ChatGroupSummary` union: the picker only needs
 * a name, an avatar and an id, and flattening it here keeps the dialog from having to branch on
 * which of the two it is holding at every turn.
 */
export interface ChatForwardTarget {
  kind: 'person' | 'group';
  /** Set for a person — what a DM is opened against when there is no conversation yet. */
  user_id?: string;
  conversation_id: string | null;
  name: string;
  avatar_url?: string | null;
  status?: ChatPresenceStatus;
  /** Group member count / a person's role — the dialog's second line. */
  subtitle?: string;
}

/** Why a single target of a forward didn't get the message. */
export type ChatForwardFailure = 'unreachable' | 'empty' | 'send';

/** Per-target outcome. A forward to several places can partly succeed. */
export interface ChatForwardResult {
  target: ChatForwardTarget;
  ok: boolean;
  reason?: ChatForwardFailure;
  /** Attachments that could not be re-fetched and so did not travel with the copy. */
  droppedAttachments: number;
}

/** Why a Quick Message send to one person didn't land. */
export type ChatQuickMessageFailure = 'unreachable' | 'send';

/**
 * Per-recipient outcome of {@link ChatStore.sendQuickMessage} — a Master Admin broadcast to
 * several or all colleagues can partly succeed, the same way a forward can.
 */
export interface ChatQuickMessageResult {
  person: ChatPerson;
  ok: boolean;
  reason?: ChatQuickMessageFailure;
}

/**
 * How far one of your own messages has got, as drawn by the tick under the bubble.
 *
 * Client-derived, not a server field: read off the message's own pending/failed flags plus the
 * read pointers in {@link ChatReadReceipt}. There is no per-device delivery signal in the
 * protocol, so there is deliberately no `delivered` state — one tick means "the server has it",
 * which is the strongest claim the data supports.
 */
export type ChatDeliveryState = 'sending' | 'failed' | 'sent' | 'read' | 'read-all';

/** Who has read up to where — drives the "Seen by …" line under your last message. */
export interface ChatReadReceipt {
  conversation_id: string;
  user_id: string;
  last_read_message_id: string;
  read_at: string;
}

/** Someone typing in a conversation. Never persisted. */
export interface ChatTypingState {
  conversation_id: string;
  user_id: string;
  name: string;
  is_typing: boolean;
}

/** Presence change pushed over the socket, or one row of `GET /chat/presence`. */
export interface ChatPresenceEvent {
  user_id: string;
  /** Effective status — `offline` whenever there is no live socket, whatever was chosen. */
  status: ChatPresenceStatus;
  /**
   * What the user actually chose, independent of whether they are connected.
   *
   * The two differ exactly when it matters: someone who set Do Not Disturb yesterday and has no
   * socket open today reads `status: "offline"`, `manual_status: "dnd"`. Restoring the switcher
   * from `status` would quietly downgrade them back to Online.
   */
  manual_status?: ChatManualStatus;
  last_seen_at?: string | null;
  /** Only on the caller's own row of `GET /chat/presence`. */
  self?: boolean;
  /**
   * Own row only: whether the server holds a live socket for *this user* right now, with no grace
   * period. `status` lags a dropped socket by ~30 s; this does not.
   */
  connected?: boolean;
}

/**
 * A Master Admin's hide/unhide pushed to every other connected client — see
 * `docs/backend/chat/internal-chat-hide-user.md` §4. Broadcast rather than polled, same as `presence`,
 * so the People list and any open picker update the moment the action lands.
 */
export interface ChatHiddenEvent {
  user_id: string;
  is_hidden: boolean;
}

/** What the composer hands to the store on submit. */
export interface ChatComposerSubmit {
  text: string;
  files: File[];
  /** Set only when the attachment is a recorded voice note, so the bubble can show its length. */
  voiceDurationMs?: number | null;
}

/**
 * A call worth ringing about, held on the store and rendered by the app-shell banner.
 *
 * Derived entirely from a message that carried a call link — there is no call event on the wire,
 * so this is the client's own reading of "someone just started a call", not something the server
 * told us. That is also why it is transient: nothing persists it, and a refresh clears it.
 */
/**
 * What happened to a call, as far as *this* client can honestly tell.
 *
 * Deliberately not a server field. Google Meet never reports who joined a room, and there is no
 * call event on the wire, so the only outcomes anyone can know are the ones this browser watched
 * happen: you opened it, you dismissed it, or it rang out unanswered. Nothing here says anything
 * about what the other people in the thread did.
 */
export type ChatCallOutcome = 'joined' | 'ignored' | 'missed';

export interface ChatIncomingCall {
  conversation_id: string;
  /** The message that carried the link, so the outcome can be recorded against it. */
  message_id: string;
  /** Who started it. */
  caller: string;
  /** The group name, or empty for a direct message where the caller's name is the whole story. */
  label: string;
  /** What Join opens. */
  url: string;
  /** "Google Meet", "Zoom", … — shown when there is no group name to show instead. */
  provider_name: string;
}

/** Realtime event names, matching §7.1 of the backend spec. */
export type ChatRealtimeAction =
  | 'message-new'
  | 'message-updated'
  | 'message-deleted'
  | 'reaction-updated'
  | 'presence'
  | 'typing'
  | 'read-receipt'
  | 'conversation-created'
  | 'conversation-updated'
  | 'conversation-removed'
  | 'unread-count'
  | 'user-hidden';

export interface ChatRealtimeEvent {
  action: ChatRealtimeAction;
  data: any;
}

/** Feature flag gating the nav item, the route and the floating launcher. */
export const CHAT_FEATURE_FLAG = 'chat';

/** Matches the server cap in §8.2 — validated client-side so the user hears about it instantly. */
export const CHAT_MAX_FILE_BYTES = 25 * 1024 * 1024;

export const CHAT_MAX_FILES_PER_MESSAGE = 10;

/**
 * Server rejects these outright (§8.2); blocking here saves a round trip.
 *
 * `svg` is not in the spec's list but the server blocks it too: an SVG is a script-bearing
 * document, and a signed S3 URL opened in a tab would execute it.
 */
export const CHAT_BLOCKED_EXTENSIONS = ['exe', 'bat', 'cmd', 'sh', 'msi', 'scr', 'js', 'svg', 'svgz'];

/** Mime equivalents of the above, for a file whose extension has been renamed away. */
export const CHAT_BLOCKED_MIMES = ['image/svg+xml', 'text/javascript', 'application/javascript', 'application/x-msdownload', 'application/x-sh'];

/**
 * The `accept` attribute for the file picker — the server's allowed list from §8.2.
 *
 * A hint, not a control: the picker's "All files" option defeats it and drag-drop ignores it
 * entirely, which is why {@link chatFileError} still runs on everything that gets staged. Its job
 * is to make the common path show the right files first.
 */
export const CHAT_ACCEPT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'application/pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.csv',
  '.txt',
  '.zip',
  'audio/webm',
  'audio/mp4',
  'audio/mpeg',
  'audio/ogg',
].join(',');

/**
 * Why a file cannot be sent, or `null` if it can.
 *
 * The same rules the server applies, run before the upload so the answer lands on the file itself
 * rather than arriving as a toast after the whole message has failed. The server still sniffs
 * content, so a renamed `.exe` is caught there — this only saves the round trip for honest files.
 */
export function chatFileError(file: File): string | null {
  const extension = (file.name.split('.').pop() || '').toLowerCase();
  const mime = (file.type || '').toLowerCase();

  if (CHAT_BLOCKED_EXTENSIONS.includes(extension) || CHAT_BLOCKED_MIMES.includes(mime)) {
    return 'This file type is not allowed';
  }
  if (file.size > CHAT_MAX_FILE_BYTES) {
    return `Too large — ${formatFileSize(file.size)} of a ${formatFileSize(CHAT_MAX_FILE_BYTES)} limit`;
  }
  return null;
}

/** Server caps the body at 8000 chars. */
export const CHAT_MAX_BODY_LENGTH = 8000;

/** Messages older than this can no longer be edited (§6.3). */
export const CHAT_EDIT_WINDOW_MS = 15 * 60 * 1000;

/**
 * How long a call link posted in a thread still counts as "the call happening right now".
 *
 * Pressing the call button inside this window joins that call instead of minting a second room —
 * without it, three people arriving at once end up in three different meetings.
 */
export const CHAT_CALL_REUSE_MS = 15 * 60 * 1000;

/**
 * How fresh a call message has to be to ring.
 *
 * A call is only worth answering while it is happening, so anything that arrives later than this
 * — a thread opened for the first time, messages replayed after a reconnect — lands as an ordinary
 * message rather than ringing a phone for a call that finished twenty minutes ago.
 */
export const CHAT_CALL_RING_WINDOW_MS = 60 * 1000;

/** How long the ring keeps going before it gives up on its own. */
export const CHAT_CALL_RING_TIMEOUT_MS = 30 * 1000;

/**
 * How recently the thread must have been told about a call for a re-share to stay quiet.
 *
 * Pressing the call button on a live call posts the link again, which rings everyone — useful as a
 * nudge, miserable if an impatient double-press rings the office twice. Inside this window the
 * thread has just been told, so the button only opens the call.
 */
export const CHAT_CALL_RESHARE_COOLDOWN_MS = 2 * 60 * 1000;

/**
 * How far back the Calls filter looks.
 *
 * A week, because the list answers "did I miss anything?" rather than "what have we ever done" —
 * and because the client can only see calls it has actually loaded, so a longer window would
 * promise a completeness that isn't there.
 */
export const CHAT_CALL_HISTORY_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * How many call outcomes to keep.
 *
 * They live in local storage and nothing ever deletes one, so the map needs a ceiling. Oldest
 * entries fall off first; losing the outcome of a call from months ago costs nothing.
 */
export const CHAT_CALL_OUTCOME_LIMIT = 200;

/**
 * How far back the transcript goes. Two weeks.
 *
 * `docs/backend/chat/internal-chat-message-retention.md` asks for a 14-day purge server-side; until that
 * job exists the server still returns everything it has ever stored, so the window is enforced here
 * as well. Strictly *older than* this is dropped — a message inside 14 days is always kept.
 *
 * This is a display window, not a delete: rows stay in Mongo and attachments stay in S3 until the
 * backend job lands. Keep both halves — dropping this once the purge ships would only matter for
 * the minutes between a message ageing out and the nightly sweep, but the sweep is the thing that
 * actually reclaims storage.
 */
export const CHAT_MESSAGE_RETENTION_MS = 14 * 24 * 60 * 60 * 1000;

/** The one-tap reaction row. Anything else comes from the emoji picker. */
export const CHAT_QUICK_REACTIONS = ['👍', '❤️', '😂', '🎉', '👀', '🙏'];

/** Emoji offered by the composer picker — small and curated, not a full unicode browser. */
export const CHAT_EMOJI_PALETTE = [
  '😀',
  '😄',
  '😁',
  '😊',
  '🙂',
  '😉',
  '😍',
  '😘',
  '🤩',
  '🥳',
  '😎',
  '🤔',
  '😅',
  '😢',
  '😭',
  '😡',
  '👍',
  '👎',
  '👏',
  '🙌',
  '🙏',
  '💪',
  '🤝',
  '👀',
  '❤️',
  '🔥',
  '⭐',
  '✅',
  '❌',
  '⚠️',
  '🎉',
  '🚀',
  '📈',
  '📉',
  '💰',
  '🏠',
  '🔑',
  '📅',
  '📞',
  '✉️',
];

/** Human-readable file size, e.g. `1.4 MB`. */
export function formatFileSize(bytes: number): string {
  if (!bytes || bytes < 0) {
    return '0 KB';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const kb = bytes / 1024;
  if (kb < 1024) {
    return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  }
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

/** Classify a file for the bubble renderer (image thumb vs file row vs voice player). */
export function attachmentKindFor(file: { type?: string; name?: string }): ChatAttachmentKind {
  const mime = (file?.type || '').toLowerCase();
  if (mime.startsWith('image/')) {
    return 'image';
  }
  if (mime.startsWith('audio/')) {
    return 'voice';
  }
  return 'file';
}

/**
 * The centred grey line for a system row: "John added Sarah and Michael".
 *
 * Built from `system_meta` rather than `body`, which the server leaves empty on system events —
 * the names in the meta are captured at the time of the event, so a row still reads correctly
 * after the person named in it has left the group or the office.
 */
export function systemEventText(message: ChatMessage): string {
  const meta = message.system_meta ?? {};
  const actor = meta.actor_name || message.sender?.name || '';
  const targets = (meta.target_names ?? []).filter(Boolean);

  // The server also sends a generated plain-text sentence. It is the fallback for anything we
  // cannot phrase ourselves — an event type added after this code, or a payload whose meta is
  // missing the names — so a system row is never a blank line.
  const fallback = message.body || '';

  switch (message.system_event) {
    case 'member_added':
      return actor && targets.length ? `${actor} added ${listNames(targets)}` : fallback;
    case 'member_removed':
      return actor && targets.length ? `${actor} removed ${listNames(targets)}` : fallback;
    case 'member_left':
      return actor ? `${actor} left the group` : fallback;
    case 'group_renamed':
      if (!actor) {
        return fallback;
      }
      return meta.old_name && meta.new_name
        ? `${actor} renamed "${meta.old_name}" to "${meta.new_name}"`
        : meta.new_name
          ? `${actor} renamed the group to "${meta.new_name}"`
          : fallback || `${actor} renamed the group`;
    default:
      return fallback;
  }
}

/** "Sarah", "Sarah and Michael", "Sarah, Michael and Lisa". */
export function listNames(names: string[]): string {
  if (names.length === 1) {
    return names[0];
  }
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** Initials fallback when a user has no avatar. */
export function initialsFor(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return '?';
  }
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
